import React, { useState, useRef } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EagleviewImportModal = ({
  isOpen,
  onClose,
  estimateId,
  onSuccess
}) => {
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  const [manualData, setManualData] = useState({
    total_sqft: '',
    num_facets: '',
    pitch: '4/12',
    ridge_ft: '',
    valley_ft: '',
    eave_ft: '',
    rake_ft: '',
    hip_ft: '',
    step_flashing_ft: '',
    drip_edge_ft: ''
  });

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setExtracted(null);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleExtract = async () => {
    if (!file) {
      toast.error('Please select an Eagleview PDF file');
      return;
    }
    
    setIsExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('estimate_id', estimateId);
      
      const response = await axios.post(`${BACKEND_URL}/api/eagleview/extract-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setExtracted(response.data.measurements);
      setManualData({
        total_sqft: response.data.measurements.total_roof_area_sqft?.toString() || '',
        num_facets: response.data.measurements.number_of_facets?.toString() || '',
        pitch: response.data.measurements.predominant_pitch || '4/12',
        ridge_ft: response.data.measurements.ridge_length_ft?.toString() || '',
        valley_ft: response.data.measurements.valley_length_ft?.toString() || '',
        eave_ft: response.data.measurements.eave_length_ft?.toString() || '',
        rake_ft: response.data.measurements.rake_length_ft?.toString() || '',
        hip_ft: response.data.measurements.hip_length_ft?.toString() || '',
        step_flashing_ft: response.data.measurements.step_flashing_ft?.toString() || '',
        drip_edge_ft: response.data.measurements.drip_edge_ft?.toString() || ''
      });
      toast.success('Data extracted successfully');
    } catch (error) {
      console.error('Error extracting Eagleview PDF:', error);
      toast.error('Failed to extract data from PDF. Enter measurements manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleImport = async () => {
    const data = extracted || manualData;
    
    if (!data.total_sqft && !data.total_roof_area_sqft) {
      toast.error('Please enter at least the total square footage');
      return;
    }
    
    setIsImporting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/eagleview/manual-import/${estimateId}`, {
        total_sqft: parseFloat(data.total_sqft || data.total_roof_area_sqft || 0),
        num_facets: parseInt(data.num_facets || data.number_of_facets || 1),
        pitch: data.pitch || data.predominant_pitch || '4/12',
        ridge_ft: parseFloat(data.ridge_ft || data.ridge_length_ft || 0),
        valley_ft: parseFloat(data.valley_ft || data.valley_length_ft || 0),
        eave_ft: parseFloat(data.eave_ft || data.eave_length_ft || 0),
        rake_ft: parseFloat(data.rake_ft || data.rake_length_ft || 0),
        hip_ft: parseFloat(data.hip_ft || data.hip_length_ft || 0),
        step_flashing_ft: parseFloat(data.step_flashing_ft || 0),
        drip_edge_ft: parseFloat(data.drip_edge_ft || 0)
      });
      
      toast.success('Eagleview measurements imported!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error importing Eagleview measurements:', error);
      toast.error('Failed to import measurements');
    } finally {
      setIsImporting(false);
    }
  };

  const updateManualData = (field, value) => {
    setManualData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Import Eagleview Report
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* File Upload */}
          <div>
            <Label>Upload Eagleview PDF</Label>
            <div className="mt-2 flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1"
              >
                <Upload className="w-4 h-4 mr-2" />
                {file ? file.name : 'Select PDF File'}
              </Button>
              <Button
                onClick={handleExtract}
                disabled={!file || isExtracting}
                className="bg-blue-600"
              >
                {isExtracting ? 'Extracting...' : 'Extract Data'}
              </Button>
            </div>
          </div>

          {/* Manual Entry Form */}
          <div>
            <Label className="text-lg font-semibold">Measurements</Label>
            <p className="text-sm text-gray-500 mb-4">
              {extracted ? 'Extracted values (edit if needed):' : 'Enter measurements manually:'}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Square Feet *</Label>
                <Input
                  type="number"
                  value={manualData.total_sqft}
                  onChange={(e) => updateManualData('total_sqft', e.target.value)}
                  placeholder="e.g., 2500"
                />
              </div>
              <div>
                <Label>Number of Facets</Label>
                <Input
                  type="number"
                  value={manualData.num_facets}
                  onChange={(e) => updateManualData('num_facets', e.target.value)}
                  placeholder="e.g., 8"
                />
              </div>
              <div>
                <Label>Predominant Pitch</Label>
                <select
                  value={manualData.pitch}
                  onChange={(e) => updateManualData('pitch', e.target.value)}
                  className="w-full border rounded-lg p-2"
                >
                  {['2/12', '3/12', '4/12', '5/12', '6/12', '7/12', '8/12', '9/12', '10/12', '11/12', '12/12'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Ridge Length (ft)</Label>
                <Input
                  type="number"
                  value={manualData.ridge_ft}
                  onChange={(e) => updateManualData('ridge_ft', e.target.value)}
                />
              </div>
              <div>
                <Label>Valley Length (ft)</Label>
                <Input
                  type="number"
                  value={manualData.valley_ft}
                  onChange={(e) => updateManualData('valley_ft', e.target.value)}
                />
              </div>
              <div>
                <Label>Eave Length (ft)</Label>
                <Input
                  type="number"
                  value={manualData.eave_ft}
                  onChange={(e) => updateManualData('eave_ft', e.target.value)}
                />
              </div>
              <div>
                <Label>Rake Length (ft)</Label>
                <Input
                  type="number"
                  value={manualData.rake_ft}
                  onChange={(e) => updateManualData('rake_ft', e.target.value)}
                />
              </div>
              <div>
                <Label>Hip Length (ft)</Label>
                <Input
                  type="number"
                  value={manualData.hip_ft}
                  onChange={(e) => updateManualData('hip_ft', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={isImporting}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isImporting ? 'Importing...' : 'Import Measurements'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EagleviewImportModal;
